import React, { useEffect, useMemo, useRef, useState } from "react";
import { getHabboImageUrl } from "./HabboImage";
import { toast } from "react-toastify";
import ContentBox from './ContentBox';

const mod8 = (n) => ((Number(n) % 8) + 8) % 8;

const ACTIONS = [
    { value: "std", label: "Stand" },
    { value: "wav", label: "Wave" },
    { value: "wlk", label: "Walk" },
    { value: "sit", label: "Sit" },
    { value: "lay", label: "Lay" },
];

const GESTURES = [
    { value: "std", label: "Normal" },
    { value: "sml", label: "Smile" },
    { value: "agr", label: "Angry" },
    { value: "sad", label: "Sad" },
    { value: "srp", label: "Surprised" },
    { value: "eyb", label: "Eye Blink" },
    { value: "spk", label: "Speak" },
];

const CARRY_ITEMS = [
    { value: "", label: "None" },
    { value: "1", label: "Drink" },
    { value: "2", label: "Carrot" },
    { value: "3", label: "Ice Cream" },
    { value: "5", label: "Habbo Cola" },
    { value: "6", label: "Coffee" },
    { value: "9", label: "Love Potion" },
    { value: "11", label: "Fishing Rod" },
];

const ICON_DIR = 2;
const ICON_HEAD = 2;

const BLINK_MIN_MS = 2500;
const BLINK_MAX_MS = 5000;
const BLINK_HOLD_MS = 400;

const GRID_BG_OFFSETS = {
    b: {
        default: {
            default: { x: 1, y: 13 },
            4: { x: -3, y: 13 },
            5: { x: -3, y: 13 },
            6: { x: -3, y: 13 },
        },
        sit: {
            default: { x: 1, y: -1 },
            0: { x: 3, y: -2 },
            4: { x: -3, y: -1 },
            6: { x: -5, y: -2 },
        },
        lay: {
            default: { x: -9, y: 5 },
            4: { x: 9, y: 5 },
        },
    },
    a: {
        default: {
            default: { x: -1, y: 4 },
        },
        sit: {
            default: { x: 0, y: 12 },
            0: { x: 0, y: -5 },
            4: { x: -1, y: 12 },
            6: { x: -1, y: -5 },
        },
        lay: {
            default: { x: -6, y: 3 },
            4: { x: 6, y: 3 },
        },
    },
};

const ICON_OFFSETS = {
    head: { default: { x: 0, y: 14 } },
    expressions: { default: { x: 0, y: 14 } },
    actions: {
        default: { x: 0, y: -40 },
        sit: { x: -6, y: -30 },
        lay: { x: 10, y: 0 },
        wav: { x: -4, y: 14 },
        wlk: { x: 0, y: -40 },
    },
    carry: {
        none: { x: -6, y: -18 },
        item: { x: -6, y: -18 },
    },
    shadow: { default: { x: -2, y: -40 } },
    frankify: { default: { x: -2, y: 14 } },
    body: { default: { x: 0, y: 0 } },
};

const iconStyleFor = (group, key) => {
    const g = ICON_OFFSETS[group] || {};
    const off = (key && g[key]) || g.default || { x: 0, y: 0 };
    if (!off.x && !off.y) return undefined;
    return { transform: `translate(${off.x}px, ${off.y}px)` };
};

const FIG_CACHE_KEY = "habbo_fig_cache_v1";

const HOTEL_API_BASE = {
    COM: "https://origins.habbo.com",
    BR: "https://origins.habbo.com.br",
    ES: "https://origins.habbo.es",
};

const readFigCache = () => {
    try {
        const raw = sessionStorage.getItem(FIG_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const writeFigCache = (cache) => {
    try {
        sessionStorage.setItem(FIG_CACHE_KEY, JSON.stringify(cache));
    } catch {}
};

const inFlightLookups = new Map();

async function resolveFigureString({ username, hotel, signal }) {
    const name = (username || "").trim();
    const h = (hotel || "COM").toUpperCase();
    if (!name) throw new Error("Missing username");

    const key = `${h}:${name}`.toLowerCase();

    const cache = readFigCache();
    if (cache[key]?.figureString) return cache[key].figureString;

    if (inFlightLookups.has(key)) return inFlightLookups.get(key);

    const p = (async () => {
        const base = HOTEL_API_BASE[h] || HOTEL_API_BASE.COM;
        const url = `${base}/api/public/users?name=${encodeURIComponent(name)}`;

        const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const fig = data?.figureString;
        if (!fig) throw new Error("No figureString in response");

        const nextCache = readFigCache();
        nextCache[key] = { figureString: fig, t: Date.now() };
        writeFigCache(nextCache);

        return fig;
    })();

    inFlightLookups.set(key, p);
    try {
        return await p;
    } finally {
        inFlightLookups.delete(key);
    }
}

function RetryImg({ src, alt = "", className = "", style, maxTries = 4, ...rest }) {
    const [tryN, setTryN] = useState(0);

    useEffect(() => {
        setTryN(0);
    }, [src]);

    const bust = (u, n) => {
        if (!u) return u;
        const join = u.includes("?") ? "&" : "?";
        return `${u}${join}rt=${n}`;
    };

    return (
        <img
            src={bust(src, tryN)}
            alt={alt}
            className={className}
            style={style}
            loading="eager"
            decoding="async"
            onError={() => {
                setTryN((n) => (n < maxTries ? n + 1 : n));
            }}
            {...rest}
        />
    );
}

function IconRow({ title, selectedLabel, children }) {
    return (
        <div className="w-full">
            <div className="text-sm font-['Jersey_10'] uppercase mb-1">
                {title} <span className="opacity-70">[{selectedLabel ?? ""}]</span>
            </div>
            <div className="habbo-scroll flex gap-2 overflow-x-auto pb-2 pr-1">{children}</div>
        </div>
    );
}

function IconButton({ selected, disabled, title, onClick, src, alt = "", imgStyle }) {
    return (
        <button
            type="button"
            onClick={disabled ? undefined : onClick}
            title={title}
            disabled={disabled}
            className={[
                "shrink-0 w-12 h-12 border",
                selected ? "border-gray-800" : "border-gray-300",
                "flex items-center justify-center overflow-hidden",
                selected ? "bg-gold " : "bg-gray-200",
                disabled ? "opacity-40 cursor-not-allowed" : selected ? "hover:bg-gold" : "hover:bg-gray-300",
            ].join(" ")}
        >
            <RetryImg src={src} alt={alt} className="block max-w-none max-h-none" style={imgStyle} />
        </button>
    );
}

function ThunderButton({ on, disabled, onClick }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            title="Animate"
            className={[
                "w-10 h-10 border border-black bg-offwhite",
                "flex items-center justify-center hover:bg-gray-200",
                disabled ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
        >
            <svg viewBox="0 0 24 24" className={["w-6 h-6", on ? "" : "opacity-40"].join(" ")} fill="currentColor" aria-hidden="true">
                <path d="M13 2L3 14h7l-1 8 12-14h-7l-1-6z" />
            </svg>
        </button>
    );
}

function ZoomButton({ zoom, disabled, onClick }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            title="Zoom"
            className={[
                "w-16 h-10 border border-black bg-offwhite",
                "flex items-center justify-center gap-1 hover:bg-gray-200",
                disabled ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
        >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
                <path d="M11 8v6" />
                <path d="M8 11h6" />
            </svg>
            <span className="font-['Jersey_10'] text-sm uppercase">{zoom}x</span>
        </button>
    );
}

function BgDropdown({ value, onChange, disabled }) {
    const [open, setOpen] = useState(false);

    const previewDefault = <div className="w-10 h-10 border border-black bg-gray-200" style={{ imageRendering: "pixelated" }} />;

    const previewGrid = (
        <div
            className="w-10 h-10 border border-black"
            style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/img/red_tile.webp)`,
                backgroundRepeat: "repeat",
                backgroundSize: "64px 32px",
                backgroundPosition: "0px 0px",
                imageRendering: "pixelated",
            }}
        />
    );

    const currentPreview = value === "grid" ? previewGrid : previewDefault;

    return (
        <div className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={disabled ? undefined : () => setOpen((s) => !s)}
                title="Background"
                className={[
                    "w-10 h-10 border border-black bg-offwhite",
                    "flex items-center justify-center hover:bg-gray-200",
                    disabled ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
            >
                {currentPreview}
            </button>

            {open && !disabled && (
                <div className="absolute right-0 mt-2 z-20 border border-black bg-offwhite shadow">
                    <button
                        type="button"
                        onClick={() => {
                            onChange("default");
                            setOpen(false);
                        }}
                        className="p-2 hover:bg-gray-200 w-full flex items-center justify-center"
                        title="Default"
                    >
                        {previewDefault}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onChange("grid");
                            setOpen(false);
                        }}
                        className="p-2 hover:bg-gray-200 w-full flex items-center justify-center"
                        title="Grid"
                    >
                        {previewGrid}
                    </button>
                </div>
            )}
        </div>
    );
}

function HabboImager() {
    const DEFAULT_PLACEHOLDER_HABBO = "jejsjdbsbsjwieirjejsndbfhs";

    const [habboNameInput, setHabboNameInput] = useState("");
    const [habboName, setHabboName] = useState(DEFAULT_PLACEHOLDER_HABBO);
    const [hotel, setHotel] = useState("COM");
    const [figureString, setFigureString] = useState(null);

    const lookupAbortRef = useRef(null);

    const [direction, setDirection] = useState(2);
    const [headDirection, setHeadDirection] = useState(2);

    const [action, setAction] = useState("std");
    const [gesture, setGesture] = useState("std");
    const [size, setSize] = useState("b");

    const [shadow, setShadow] = useState(true);
    const shadowUserSetRef = useRef(false);
    const shadowSuppressedByPoseRef = useRef(false);

    const [frankify, setFrankify] = useState(false);
    const [useFrankHatForDefault, setUseFrankHatForDefault] = useState(false);

    const [crr, setCrr] = useState("");

    const [alive, setAlive] = useState(false);

    const [blinkOn, setBlinkOn] = useState(false);
    const blinkPausedRef = useRef(false);

    const [liveHeadPos, setLiveHeadPos] = useState(null);

    const [liveActionOverride, setLiveActionOverride] = useState(null);

    const [liveGestureOverride, setLiveGestureOverride] = useState(null);
    const [liveGesturePulseOn, setLiveGesturePulseOn] = useState(false);

    const headStepTimerRef = useRef(null);
    const headHoldTimerRef = useRef(null);
    const headSchedTimerRef = useRef(null);

    const actionHoldTimerRef = useRef(null);
    const actionSchedTimerRef = useRef(null);

    const gestureSeqTimerRef = useRef(null);
    const gestureSchedTimerRef = useRef(null);

    const blinkIdleTimerRef = useRef(null);
    const blinkOffTimerRef = useRef(null);

    const [zoom, setZoom] = useState(1);
    const [showZoomNotice, setShowZoomNotice] = useState(false);
    const zoomNoticeTimerRef = useRef(null);

    const [previewBg, setPreviewBg] = useState("default");

    const PREV_KEY = "habbo_prev_habbos_v2";
    const LAST_KEY = "habbo_prev_last_v1";
    const MAX_PREV = 20;

    const [prevHabbos, setPrevHabbos] = useState([]);
    const hasRealHabboRef = useRef(false);
    const hydratedRef = useRef(false);
    const restoringRef = useRef(false);

    const isValidHabboName = (name) => {
        const n = (name || "").trim();
        if (!n) return false;
        if (n === DEFAULT_PLACEHOLDER_HABBO) return false;
        return true;
    };

    const effectiveCustom = useFrankHatForDefault || frankify ? "frank_hat" : null;

    useEffect(() => {
        if (!isValidHabboName(habboName)) {
            if (figureString !== null) setFigureString(null);
            return;
        }

        const key = `${(hotel || "COM").toUpperCase()}:${habboName}`.toLowerCase();
        const cache = readFigCache();
        const cachedFig = cache[key]?.figureString;

        if (cachedFig) {
            if (figureString !== cachedFig) setFigureString(cachedFig);
            return;
        }

        if (figureString) {
            const nextCache = readFigCache();
            nextCache[key] = { figureString, t: Date.now() };
            writeFigCache(nextCache);
            return;
        }

        if (lookupAbortRef.current) lookupAbortRef.current.abort();
        const ac = new AbortController();
        lookupAbortRef.current = ac;

        resolveFigureString({ username: habboName, hotel, signal: ac.signal })
            .then((fig) => {
                if (ac.signal.aborted) return;
                setFigureString(fig);
            })
            .catch(() => {
                if (ac.signal.aborted) return;
                setFigureString(null);
            });

        return () => {
            ac.abort();
        };
    }, [habboName, hotel, figureString]);

    const identityParams = useMemo(() => {
        if (figureString) return { habbo: null, figure: figureString };
        return { habbo: habboName, figure: null };
    }, [figureString, habboName]);

    const makeSnapshot = (overrides = {}) => {
        const snap = {
            id: String(Date.now()) + "_" + Math.random().toString(16).slice(2),
            habboName,
            hotel,
            figureString: figureString || null,
            direction,
            headDirection,
            action,
            gesture,
            size,
            crr,
            shadow,
            frankify,
            useFrankHatForDefault,
            zoom,
            previewBg,
            ...overrides,
        };

        const custom = snap.useFrankHatForDefault || snap.frankify ? "frank_hat" : null;

        snap.thumbUrl = getHabboImageUrl({
            habbo: snap.figureString ? null : snap.habboName,
            figure: snap.figureString || null,
            hotel: snap.hotel,
            direction: snap.direction,
            head_direction: snap.headDirection,
            action: snap.action,
            gesture: snap.gesture,
            size: snap.size,
            shadow: snap.shadow,
            crr: snap.crr || null,
            custom,
        });

        return snap;
    };

    const loadPrevFromSession = () => {
        try {
            const raw = sessionStorage.getItem(PREV_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            if (!Array.isArray(parsed)) return [];
            return parsed.filter((x) => x && typeof x === "object" && isValidHabboName(x.habboName));
        } catch {
            return [];
        }
    };

    const savePrevToSession = (list) => {
        try {
            sessionStorage.setItem(PREV_KEY, JSON.stringify(list));
        } catch {}
    };

    const loadLastFromSession = () => {
        try {
            const raw = sessionStorage.getItem(LAST_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== "object") return null;
            if (!isValidHabboName(parsed.habboName)) return null;
            return parsed;
        } catch {
            return null;
        }
    };

    const saveLastToSession = (snap) => {
        try {
            sessionStorage.setItem(LAST_KEY, JSON.stringify(snap));
        } catch {}
    };

    const pushHistory = (snap) => {
        if (!snap || !isValidHabboName(snap.habboName)) return;
        const key = `${snap.hotel}:${snap.habboName}`.toLowerCase();
        setPrevHabbos((prev) => {
            const next = [snap, ...prev.filter((x) => `${x.hotel}:${x.habboName}`.toLowerCase() !== key)].slice(0, Math.max(0, MAX_PREV - 1));
            return next;
        });
    };

    const applySnapshot = (snap) => {
        setAlive(false);
        setHabboName(snap.habboName);
        setHotel(snap.hotel);
        setFigureString(snap.figureString || null);

        setDirection(snap.direction);
        setHeadDirection(snap.headDirection);
        setAction(snap.action);
        setGesture(snap.gesture);
        setSize(snap.size);

        setCrr(snap.crr || "");
        setShadow(!!snap.shadow);
        setFrankify(!!snap.frankify);
        setUseFrankHatForDefault(!!snap.useFrankHatForDefault);

        setZoom(snap.zoom || 1);
        setPreviewBg(snap.previewBg || "default");
    };

    useEffect(() => {
        const loadedPrev = loadPrevFromSession();
        setPrevHabbos(loadedPrev.slice(0, Math.max(0, MAX_PREV - 1)));

        const last = loadLastFromSession();
        if (!last) {
            hydratedRef.current = true;
            return;
        }

        restoringRef.current = true;
        applySnapshot(last);
        hasRealHabboRef.current = true;

        const lastKey = `${last.hotel}:${last.habboName}`.toLowerCase();
        setPrevHabbos((prev) => prev.filter((x) => `${x.hotel}:${x.habboName}`.toLowerCase() !== lastKey));

        hydratedRef.current = true;
    }, []);

    useEffect(() => {
        savePrevToSession(prevHabbos);
    }, [prevHabbos]);

    const resetPreviewForNewHabbo = () => {
        shadowUserSetRef.current = false;
        shadowSuppressedByPoseRef.current = false;

        setAlive(false);
        setBlinkOn(false);

        setDirection(2);
        setHeadDirection(2);
        setAction("std");
        setGesture("std");
        setSize("b");
        setCrr("");

        setShadow(true);
        setFrankify(false);
        setUseFrankHatForDefault(false);

        setZoom(1);
        setPreviewBg("default");

        setShowZoomNotice(false);
        if (zoomNoticeTimerRef.current) window.clearTimeout(zoomNoticeTimerRef.current);
        zoomNoticeTimerRef.current = null;

        setFigureString(null);
    };

    const currentPreviewSnap = useMemo(
        () =>
            makeSnapshot({
                id: "__current__",
            }),
        [habboName, hotel, figureString, direction, headDirection, action, gesture, size, crr, shadow, frankify, useFrankHatForDefault, zoom, previewBg]
    );

    useEffect(() => {
        if (!hydratedRef.current) return;
        if (!hasRealHabboRef.current) return;
        if (!isValidHabboName(habboName)) return;
        if (!currentPreviewSnap?.habboName) return;

        const snapToSave = { ...currentPreviewSnap, id: String(Date.now()) + "_" + Math.random().toString(16).slice(2) };
        saveLastToSession(snapToSave);

        if (restoringRef.current) restoringRef.current = false;
    }, [currentPreviewSnap, habboName]);

    const selectPrevHabbo = (snap) => {
        if (hasRealHabboRef.current && !restoringRef.current) pushHistory(makeSnapshot());
        setPrevHabbos((prev) => prev.filter((x) => x.id !== snap.id));
        restoringRef.current = false;
        applySnapshot(snap);
        hasRealHabboRef.current = true;
    };

    const PreviousHabbosPanel = ({ className = "" }) => {
        const currentKey = `${hotel}:${habboName}`.toLowerCase();
        const showCurrent = hasRealHabboRef.current && isValidHabboName(habboName);
        const items = (showCurrent ? [currentPreviewSnap] : []).concat(prevHabbos).slice(0, MAX_PREV);

        return (
            <div className={["w-full", className].join(" ")}>
                <div className="text-sm font-['Jersey_10'] uppercase mb-1">
                    Previous Habbos! <span className="opacity-70">[{Math.min(items.length, MAX_PREV)}/{MAX_PREV}]</span>
                </div>

                <div className="habbo-scroll flex gap-2 overflow-x-auto pb-2 pr-1">
                    {items.map((s) => {
                        const key = `${s.hotel}:${s.habboName}`.toLowerCase();
                        const selected = key === currentKey && s.id === "__current__";
                        const clickable = s.id !== "__current__";

                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={clickable ? () => selectPrevHabbo(s) : undefined}
                                title={`${s.habboName} (${s.hotel})`}
                                className={[
                                    "shrink-0 min-w-12 w-12 h-28 border",
                                    selected ? "border-gray-800" : "border-gray-300",
                                    "flex items-center justify-center overflow-hidden",
                                    selected ? "bg-gold" : clickable ? "bg-gray-200 hover:bg-gray-300" : "bg-gray-200",
                                ].join(" ")}
                            >
                                <RetryImg src={s.thumbUrl} alt="" className="block max-w-none max-h-none" style={{ imageRendering: "pixelated" }} />
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const previewBgConfig = useMemo(
        () => ({
            default: { type: "flat" },
            grid: {
                type: "tile",
                url: `${process.env.PUBLIC_URL}/img/red_tile.webp`,
                size: { w: 64, h: 32 },
            },
        }),
        []
    );

    const [cacheEpoch, setCacheEpoch] = useState(0);
    const imgCacheRef = useRef(new Set());

    useEffect(() => {
        setCacheEpoch((n) => n + 1);
        imgCacheRef.current = new Set();
    }, [habboName, hotel, figureString, effectiveCustom]);

    const withCache = (url) => {
        const join = url.includes("?") ? "&" : "?";
        return `${url}${join}cb=${cacheEpoch}`;
    };

    const ensureCached = (url) => {
        const cache = imgCacheRef.current;
        if (cache.has(url)) return;
        cache.add(url);
        const img = new Image();
        img.src = url;
    };

    const iconBaseParams = useMemo(
        () => ({
            ...identityParams,
            hotel,
            direction: ICON_DIR,
            head_direction: ICON_HEAD,
            action: "std",
            gesture: "std",
            size: "b",
            shadow: true,
            crr: null,
            custom: null,
        }),
        [identityParams, hotel]
    );

    const iconUrl = (overrides = {}) => {
        const forced = overrides?.action === "sit" || overrides?.action === "lay" ? { ...overrides, shadow: false } : overrides;
        return withCache(getHabboImageUrl({ ...iconBaseParams, ...forced }));
    };

    const allowedHeadDirections = useMemo(() => {
        const base = direction;
        return [mod8(base - 2), mod8(base - 1), mod8(base), mod8(base + 1), mod8(base + 2)];
    }, [direction]);

    const isSitOrLay = action === "sit" || action === "lay";
    const canHoldAnyItem = !isSitOrLay;

    const isDirectionAllowedForRod = direction === 2 || direction === 4;
    const canHoldFishingRod = canHoldAnyItem && isDirectionAllowedForRod;

    const setBodyDirection = (d) => {
        const next = mod8(d);
        setDirection(next);
        setHeadDirection(next);
    };

    useEffect(() => {
        if (!allowedHeadDirections.includes(headDirection)) setHeadDirection(direction);
    }, [direction, allowedHeadDirections, headDirection]);

    useEffect(() => {
        if (isSitOrLay && crr) setCrr("");
    }, [isSitOrLay, crr]);

    useEffect(() => {
        if (crr === "11" && !canHoldFishingRod) setCrr("");
    }, [crr, canHoldFishingRod]);

    useEffect(() => {
        if (action === "sit" || action === "lay") {
            if (shadow) shadowSuppressedByPoseRef.current = true;
            if (shadow) setShadow(false);
            return;
        }
        if (shadowSuppressedByPoseRef.current && !shadowUserSetRef.current) setShadow(true);
        shadowSuppressedByPoseRef.current = false;
    }, [action, shadow]);

    const setShadowSafe = (next) => {
        shadowUserSetRef.current = true;
        shadowSuppressedByPoseRef.current = false;
        setShadow(next);
    };

    const headDirOptions = useMemo(() => {
        if (action === "lay") return [2, 4];
        return allowedHeadDirections;
    }, [action, allowedHeadDirections]);

    useEffect(() => {
        if (!alive) {
            setBlinkOn(false);
            blinkPausedRef.current = false;
            return;
        }

        let cancelled = false;

        const rand = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

        const schedule = () => {
            if (cancelled) return;

            blinkIdleTimerRef.current = window.setTimeout(() => {
                if (cancelled) return;

                if (blinkPausedRef.current) {
                    schedule();
                    return;
                }

                setBlinkOn(true);

                blinkOffTimerRef.current = window.setTimeout(() => {
                    if (cancelled) return;
                    setBlinkOn(false);
                    schedule();
                }, BLINK_HOLD_MS);
            }, rand(BLINK_MIN_MS, BLINK_MAX_MS));
        };

        schedule();

        return () => {
            cancelled = true;
            if (blinkIdleTimerRef.current) window.clearTimeout(blinkIdleTimerRef.current);
            if (blinkOffTimerRef.current) window.clearTimeout(blinkOffTimerRef.current);
            blinkIdleTimerRef.current = null;
            blinkOffTimerRef.current = null;
        };
    }, [alive]);

    useEffect(() => {
        if (!alive) {
            setLiveHeadPos(null);
            if (headStepTimerRef.current) window.clearTimeout(headStepTimerRef.current);
            if (headHoldTimerRef.current) window.clearTimeout(headHoldTimerRef.current);
            if (headSchedTimerRef.current) window.clearTimeout(headSchedTimerRef.current);
            headStepTimerRef.current = null;
            headHoldTimerRef.current = null;
            headSchedTimerRef.current = null;
            return;
        }

        const rand = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

        const center = headDirection;
        const left = mod8(center - 1);
        const right = mod8(center + 1);

        const allowed = new Set(headDirOptions);
        const hasLeft = allowed.has(left);
        const hasRight = allowed.has(right);

        const getTriad = () => {
            const arr = [];
            if (hasLeft) arr.push(left);
            arr.push(center);
            if (hasRight) arr.push(right);
            return arr;
        };

        const triad = getTriad();

        const stepTo = (from, to, stepMs) => {
            if (from === to) return;
            headStepTimerRef.current = window.setTimeout(() => setLiveHeadPos(to), stepMs);
        };

        const clearStep = () => {
            if (headStepTimerRef.current) window.clearTimeout(headStepTimerRef.current);
            headStepTimerRef.current = null;
        };

        const clearHold = () => {
            if (headHoldTimerRef.current) window.clearTimeout(headHoldTimerRef.current);
            headHoldTimerRef.current = null;
        };

        const clearSched = () => {
            if (headSchedTimerRef.current) window.clearTimeout(headSchedTimerRef.current);
            headSchedTimerRef.current = null;
        };

        const schedule = () => {
            clearSched();

            headSchedTimerRef.current = window.setTimeout(() => {
                const doQuick = Math.random() < 0.18;
                const holdMs = doQuick ? rand(450, 850) : rand(2200, 4200);
                const stepMs = rand(260, 420);

                if (triad.length < 2) {
                    schedule();
                    return;
                }

                const current = liveHeadPos == null ? center : liveHeadPos;
                const targetChoices = triad.filter((x) => x !== center);
                const target = targetChoices.length ? targetChoices[Math.floor(Math.random() * targetChoices.length)] : center;

                clearStep();
                clearHold();

                const go = () => {
                    const now = liveHeadPos == null ? center : liveHeadPos;

                    if (now !== center && target !== center && now !== center && target !== now) {
                        setLiveHeadPos(center);
                        stepTo(center, target, stepMs);
                        headHoldTimerRef.current = window.setTimeout(() => setLiveHeadPos(null), holdMs + stepMs);
                        return;
                    }

                    if (now !== center && target === center) {
                        setLiveHeadPos(center);
                        headHoldTimerRef.current = window.setTimeout(() => setLiveHeadPos(null), holdMs);
                        return;
                    }

                    if (now === center && target !== center) {
                        setLiveHeadPos(target);
                        headHoldTimerRef.current = window.setTimeout(() => setLiveHeadPos(null), holdMs);
                        return;
                    }

                    setLiveHeadPos(null);
                };

                if (current === left && target === right) {
                    setLiveHeadPos(center);
                    stepTo(center, right, stepMs);
                    headHoldTimerRef.current = window.setTimeout(() => setLiveHeadPos(null), holdMs + stepMs);
                } else if (current === right && target === left) {
                    setLiveHeadPos(center);
                    stepTo(center, left, stepMs);
                    headHoldTimerRef.current = window.setTimeout(() => setLiveHeadPos(null), holdMs + stepMs);
                } else {
                    go();
                }

                schedule();
            }, rand(2400, 5200));
        };

        schedule();

        return () => {
            clearStep();
            clearHold();
            clearSched();
        };
    }, [alive, headDirection, headDirOptions, liveHeadPos]);

    useEffect(() => {
        if (!alive) {
            setLiveActionOverride(null);
            if (actionHoldTimerRef.current) window.clearTimeout(actionHoldTimerRef.current);
            if (actionSchedTimerRef.current) window.clearTimeout(actionSchedTimerRef.current);
            actionHoldTimerRef.current = null;
            actionSchedTimerRef.current = null;
            return;
        }

        const rand = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

        const schedule = () => {
            actionSchedTimerRef.current = window.setTimeout(() => {
                if (action !== "std") {
                    schedule();
                    return;
                }

                const pickA = Math.random() < 0.55 ? "wlk" : "wav";
                setLiveActionOverride(pickA);

                actionHoldTimerRef.current = window.setTimeout(() => {
                    setLiveActionOverride(null);
                    schedule();
                }, rand(650, 1100));
            }, rand(7000, 13000));
        };

        schedule();

        return () => {
            if (actionHoldTimerRef.current) window.clearTimeout(actionHoldTimerRef.current);
            if (actionSchedTimerRef.current) window.clearTimeout(actionSchedTimerRef.current);
            actionHoldTimerRef.current = null;
            actionSchedTimerRef.current = null;
        };
    }, [alive, action]);

    useEffect(() => {
        if (!alive) {
            setLiveGestureOverride(null);
            setLiveGesturePulseOn(false);
            blinkPausedRef.current = false;
            if (gestureSeqTimerRef.current) window.clearTimeout(gestureSeqTimerRef.current);
            if (gestureSchedTimerRef.current) window.clearTimeout(gestureSchedTimerRef.current);
            gestureSeqTimerRef.current = null;
            gestureSchedTimerRef.current = null;
            return;
        }

        const rand = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

        const schedule = () => {
            gestureSchedTimerRef.current = window.setTimeout(() => {
                if (gesture !== "std") {
                    schedule();
                    return;
                }

                const pool = ["spk", "sml", "agr", "sad", "srp"];
                const target = pool[Math.floor(Math.random() * pool.length)];

                blinkPausedRef.current = true;
                setBlinkOn(false);

                if (target === "spk") {
                    const pulses = 4 + Math.floor(Math.random() * 4);
                    let i = 0;

                    setLiveGestureOverride("spk");
                    setLiveGesturePulseOn(true);

                    const tick = () => {
                        i += 1;
                        setLiveGesturePulseOn((s) => !s);
                        if (i >= pulses * 2) {
                            setLiveGestureOverride(null);
                            setLiveGesturePulseOn(false);
                            blinkPausedRef.current = false;
                            schedule();
                            return;
                        }
                        gestureSeqTimerRef.current = window.setTimeout(tick, rand(110, 170));
                    };

                    gestureSeqTimerRef.current = window.setTimeout(tick, rand(110, 170));
                    return;
                }

                setLiveGestureOverride(target);
                setLiveGesturePulseOn(false);

                gestureSeqTimerRef.current = window.setTimeout(() => {
                    setLiveGestureOverride(null);
                    blinkPausedRef.current = false;
                    schedule();
                }, rand(1500, 2600));
            }, rand(5000, 15000));
        };

        schedule();

        return () => {
            if (gestureSeqTimerRef.current) window.clearTimeout(gestureSeqTimerRef.current);
            if (gestureSchedTimerRef.current) window.clearTimeout(gestureSchedTimerRef.current);
            gestureSeqTimerRef.current = null;
            gestureSchedTimerRef.current = null;
            blinkPausedRef.current = false;
        };
    }, [alive, gesture]);

    const loadHabbo = () => {
        const next = (habboNameInput || "").trim();
        if (!next) return;

        if (hasRealHabboRef.current && isValidHabboName(habboName) && !restoringRef.current) pushHistory(makeSnapshot());

        resetPreviewForNewHabbo();
        restoringRef.current = false;
        setHabboName(next);

        hasRealHabboRef.current = true;

        setHabboNameInput("");
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") loadHabbo();
    };

    const effectiveHeadDirection = alive && liveHeadPos != null ? liveHeadPos : headDirection;
    const effectiveAction = alive && liveActionOverride ? liveActionOverride : action;

    const effectiveGesture =
        alive && liveGestureOverride
            ? liveGestureOverride === "spk"
                ? liveGesturePulseOn
                    ? "spk"
                    : "std"
                : liveGestureOverride
            : gesture;

    const imageGesture = alive && !blinkPausedRef.current && blinkOn ? "eyb" : effectiveGesture;

    const baseParams = useMemo(
        () => ({
            ...identityParams,
            hotel,
            direction,
            head_direction: effectiveHeadDirection,
            action: effectiveAction,
            size,
            shadow,
            crr: crr || null,
            custom: effectiveCustom,
        }),
        [identityParams, hotel, direction, effectiveHeadDirection, effectiveAction, size, shadow, crr, effectiveCustom]
    );

    const basePreviewUrl = useMemo(() => withCache(getHabboImageUrl({ ...baseParams, gesture: effectiveGesture })), [baseParams, effectiveGesture, cacheEpoch]);
    const blinkPreviewUrl = useMemo(() => withCache(getHabboImageUrl({ ...baseParams, gesture: "eyb" })), [baseParams, cacheEpoch]);

    useEffect(() => {
        ensureCached(basePreviewUrl);
        ensureCached(blinkPreviewUrl);
    }, [basePreviewUrl, blinkPreviewUrl]);

    useEffect(() => {
        const allowed = action === "lay" ? [2, 4] : action === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        if (!allowed.includes(direction)) setBodyDirection(allowed[0]);
    }, [action, direction]);

    const imageUrl = useMemo(() => {
        if (imageGesture === "eyb") return blinkPreviewUrl;
        return basePreviewUrl;
    }, [imageGesture, basePreviewUrl, blinkPreviewUrl]);

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(imageUrl);
            toast.success("URL copied to clipboard!");
        } catch (e) {
            toast.error("Failed to copy URL.");
        }
    };

    const setActionSafe = (nextAction) => {
        if (crr && (nextAction === "sit" || nextAction === "lay")) return;
        setAction(nextAction);
    };

    const setCarrySafe = (nextCrr) => {
        if (!nextCrr) {
            setCrr("");
            return;
        }
        if (!canHoldAnyItem) return;
        if (nextCrr === "11" && !canHoldFishingRod) return;
        setCrr(nextCrr);
    };

    const resetAll = () => {
        shadowUserSetRef.current = false;
        shadowSuppressedByPoseRef.current = false;

        setDirection(2);
        setHeadDirection(2);
        setAction("std");
        setGesture("std");
        setSize("b");
        setShadow(true);
        setFrankify(false);
        setUseFrankHatForDefault(false);
        setCrr("");
        setAlive(false);
        setBlinkOn(false);
        setZoom(1);
        setPreviewBg("default");
        setShowZoomNotice(false);
        if (zoomNoticeTimerRef.current) window.clearTimeout(zoomNoticeTimerRef.current);
        zoomNoticeTimerRef.current = null;
    };

    const selectedActionLabel = useMemo(() => ACTIONS.find((a) => a.value === action)?.label || "Stand", [action]);
    const selectedGestureLabel = useMemo(() => GESTURES.find((g) => g.value === gesture)?.label || "Normal", [gesture]);
    const selectedCarryLabel = useMemo(() => CARRY_ITEMS.find((c) => c.value === crr)?.label || "None", [crr]);

    const animateDisabled = gesture === "eyb";

    const bodyDirOptions = useMemo(() => {
        const allowed = action === "lay" ? [2, 4] : action === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        const start = 2;
        return [...allowed].sort((a, b) => mod8(a - start) - mod8(b - start));
    }, [action]);

    const rotationPoseAction = action === "sit" || action === "lay" ? action : "std";
    const rotationShadow = rotationPoseAction === "sit" || rotationPoseAction === "lay" ? false : true;

    const bodyRotIconUrl = (d) =>
        withCache(
            getHabboImageUrl({
                ...identityParams,
                hotel,
                direction: d,
                head_direction: d,
                action: rotationPoseAction,
                gesture: "std",
                size: "a",
                shadow: rotationShadow,
                crr: null,
                custom: null,
            })
        );

    const headRotIconUrl = (d) =>
        withCache(
            getHabboImageUrl({
                ...identityParams,
                hotel,
                direction: rotationPoseAction === "lay" ? d : ICON_DIR,
                head_direction: d,
                action: rotationPoseAction,
                gesture: "std",
                size: "b",
                shadow: rotationShadow,
                crr: null,
                custom: null,
            })
        );

    const cycleZoom = () => {
        const next = zoom === 1 ? 2 : zoom === 2 ? 4 : 1;
        setZoom(next);

        setShowZoomNotice(true);
        if (zoomNoticeTimerRef.current) window.clearTimeout(zoomNoticeTimerRef.current);
        zoomNoticeTimerRef.current = window.setTimeout(() => setShowZoomNotice(false), 2600);
    };

    const activeBg = previewBgConfig[previewBg] || previewBgConfig.default;

    const poseKey = action === "sit" ? "sit" : action === "lay" ? "lay" : "default";

    const gridOffsetsForSize = GRID_BG_OFFSETS[size] || GRID_BG_OFFSETS.b;
    const poseOffsets = gridOffsetsForSize[poseKey] ?? gridOffsetsForSize.default ?? {};

    const gridOffset =
        typeof poseOffsets?.x === "number" || typeof poseOffsets?.y === "number" ? poseOffsets : poseOffsets?.[direction] || poseOffsets?.default || { x: 0, y: 0 };

    const tileScale = size === "a" ? 0.5 : 1;

    const bgPosition = useMemo(() => {
        if (activeBg.type !== "tile") return undefined;
        const ox = (gridOffset.x || 0) * zoom;
        const oy = (gridOffset.y || 0) * zoom;
        return `calc(50% + ${ox}px) calc(50% + ${oy}px)`;
    }, [activeBg, gridOffset, zoom]);

    const previewBgStyle =
        activeBg.type === "tile"
            ? {
                backgroundImage: `url(${activeBg.url})`,
                backgroundRepeat: "repeat",
                backgroundSize: `${activeBg.size.w * tileScale * zoom}px ${activeBg.size.h * tileScale * zoom}px`,
                backgroundPosition: bgPosition,
                imageRendering: "pixelated",
            }
            : { imageRendering: "pixelated" };

    const expressionsLocked = alive;
    const actionsLocked = alive;
    const headRotationLocked = alive;

    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const randomise = () => {
        if (alive) setAlive(false);

        const nextAction = pick(ACTIONS.map((a) => a.value));
        setAction(nextAction);

        const allowedBody = nextAction === "lay" ? [2, 4] : nextAction === "sit" ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6, 7];
        const nextDir = pick(allowedBody);

        setDirection(nextDir);
        setHeadDirection(nextDir);

        const allowedHead = nextAction === "lay" ? [2, 4] : [mod8(nextDir - 1), mod8(nextDir), mod8(nextDir + 1)];
        setHeadDirection(pick(allowedHead));

        const gesturePool = GESTURES.filter((g) => g.value !== "eyb").map((g) => g.value);
        setGesture(pick(gesturePool));

        if (nextAction === "sit" || nextAction === "lay") {
            setCrr("");
        } else {
            const canRod = nextDir === 2 || nextDir === 4;
            const carryPool = CARRY_ITEMS.filter((c) => c.value !== "11" || canRod).map((c) => c.value);
            setCrr(pick(carryPool));
        }
    };

    return (
        <div className="w-full">
            <style>{`
        .habbo-scroll::-webkit-scrollbar { height: 16px; }
        .habbo-scroll::-webkit-scrollbar-track { background: #cfcfcf; border: 1px solid #000; }
        .habbo-scroll::-webkit-scrollbar-thumb { background: #8f8f8f; border: 1px solid #000; }
        .habbo-scroll::-webkit-scrollbar-thumb:hover { background: #7a7a7a; }

        .habbo-vert-range {
          writing-mode: bt-lr;
          -webkit-appearance: slider-vertical;
        }
      `}</style>

            <ContentBox theme="default" icon="./img/pixel.png" header="Frankly Origins Basic Imager" className="select-none">

                <div className="grid grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)] gap-6 lg:items-stretch">
                    <div className="order-1 lg:order-2 min-w-0 flex flex-col gap-4 lg:h-full">
                        <div className="relative bg-gray-200 py-6 px-0 rounded flex items-center justify-center w-full min-h-[500px] overflow-hidden" style={previewBgStyle}>
                            <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
                                <BgDropdown value={previewBg} onChange={setPreviewBg} disabled={false} />
                                <ZoomButton zoom={zoom} disabled={false} onClick={cycleZoom} />
                                <ThunderButton on={alive} disabled={animateDisabled} onClick={() => setAlive((s) => !s)} />
                            </div>

                            <div style={{ transform: `scale(${zoom})`, transformOrigin: "center", imageRendering: "pixelated" }}>
                                <RetryImg src={imageUrl} alt="" className="max-w-full max-h-full image-rendering-pixelated" style={{ imageRendering: "pixelated" }} maxTries={6} />
                            </div>

                            {showZoomNotice && (
                                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white px-3 py-2 text-xs">
                                    Zoom is purely visual, copying this image won&apos;t copy the scale. You can always take a screenshot instead!
                                </div>
                            )}
                        </div>

                        <div className="w-full">
                            <label className="block text-sm font-['Jersey_10'] uppercase mb-2">Image URL:</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input type="text" value={imageUrl} readOnly className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs bg-white" onClick={(e) => e.target.select()} />
                                <div className="flex gap-2">
                                    <button
                                        onClick={copyToClipboard}
                                        className="bg-maroon text-white px-4 py-2 font-['Jersey_10'] uppercase text-sm hover:opacity-90"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                                    >
                                        Copy URL
                                    </button>

                                    <button type="button" onClick={resetAll} className="px-10 h-12 border border-black bg-red-500 text-offwhite hover:text-black hover:bg-yellow-500 font-['Jersey_10'] uppercase text-sm">
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>

                        <PreviousHabbosPanel className="hidden lg:block" />
                    </div>

                    <div className="order-2 lg:order-1 min-w-0 flex flex-col gap-4 lg:h-full">
                        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
                            <div>
                                <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Habbo Name</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={habboNameInput}
                                        onChange={(e) => setHabboNameInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm placeholder:text-gray-500 placeholder:drop-shadow-[1px_1px_0_rgba(0,0,0,0.35)]"
                                        placeholder="Username..."
                                    />
                                    <button
                                        onClick={loadHabbo}
                                        className="bg-maroon text-white px-4 py-2 font-['Jersey_10'] uppercase text-sm hover:opacity-90 whitespace-nowrap"
                                        style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                                    >
                                        Load
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Hotel</label>
                                <select value={hotel} onChange={(e) => setHotel(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm uppercase">
                                    <option value="COM">COM</option>
                                    <option value="BR">BR</option>
                                    <option value="ES">ES</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Size</label>
                                <select value={size} onChange={(e) => setSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm">
                                    <option value="a">Small</option>
                                    <option value="b">Normal</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-['Jersey_10'] uppercase mb-1">Carry Item</label>
                                <select value={crr} onChange={(e) => setCarrySafe(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" disabled={!canHoldAnyItem}>
                                    {CARRY_ITEMS.map((x) => {
                                        const disabled = !x.value ? false : !canHoldAnyItem || (x.value === "11" && !canHoldFishingRod);
                                        return (
                                            <option key={x.value || "none"} value={x.value} disabled={disabled}>
                                                {x.label}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col gap-4">
                            <IconRow title="Body Rotation" selectedLabel={String(direction)}>
                                {bodyDirOptions.map((d) => (
                                    <IconButton key={`body-${d}`} selected={direction === d} title={`Direction ${d}`} onClick={() => setBodyDirection(d)} src={bodyRotIconUrl(d)} imgStyle={iconStyleFor("body")} />
                                ))}
                            </IconRow>

                            <IconRow title="Head Rotation" selectedLabel={String(headDirection)}>
                                {headDirOptions.map((d) => (
                                    <IconButton
                                        key={`head-${d}`}
                                        selected={headDirection === d}
                                        disabled={headRotationLocked}
                                        title={`Head ${d}`}
                                        onClick={() => setHeadDirection(d)}
                                        src={headRotIconUrl(d)}
                                        imgStyle={iconStyleFor("head")}
                                    />
                                ))}
                            </IconRow>

                            <IconRow title="Actions" selectedLabel={selectedActionLabel}>
                                {ACTIONS.map((a) => {
                                    const disabled = actionsLocked || (!!crr && (a.value === "sit" || a.value === "lay"));
                                    return <IconButton key={a.value} selected={action === a.value} disabled={disabled} title={a.label} onClick={() => setActionSafe(a.value)} src={iconUrl({ action: a.value })} imgStyle={iconStyleFor("actions", a.value)} />;
                                })}
                            </IconRow>

                            <IconRow title="Expressions" selectedLabel={selectedGestureLabel}>
                                {GESTURES.map((g) => (
                                    <IconButton key={g.value} selected={gesture === g.value} disabled={expressionsLocked} title={g.label} onClick={() => setGesture(g.value)} src={iconUrl({ gesture: g.value })} imgStyle={iconStyleFor("expressions")} />
                                ))}
                            </IconRow>

                            {!isSitOrLay && (
                                <IconRow title="Carry Item" selectedLabel={selectedCarryLabel}>
                                    {CARRY_ITEMS.map((it) => {
                                        const disabled = !!it.value && (!canHoldAnyItem || (it.value === "11" && !canHoldFishingRod));
                                        const previewCarry = disabled ? null : it.value || null;
                                        const offsetKey = it.value === "" ? "none" : "item";
                                        return <IconButton key={`crr-${it.value || "none"}`} selected={crr === it.value} disabled={disabled} title={it.label} onClick={() => setCarrySafe(it.value)} src={iconUrl({ crr: previewCarry })} imgStyle={iconStyleFor("carry", offsetKey)} />;
                                    })}
                                </IconRow>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                                <IconRow title="Shadow" selectedLabel={shadow ? "On" : "Off"}>
                                    <IconButton selected={shadow === true} title="Shadow On" onClick={() => setShadowSafe(true)} src={iconUrl({ shadow: true })} imgStyle={iconStyleFor("shadow")} />
                                    <IconButton selected={shadow === false} title="Shadow Off" onClick={() => setShadowSafe(false)} src={iconUrl({ shadow: false })} imgStyle={iconStyleFor("shadow")} />
                                </IconRow>

                                <IconRow title="Frankify" selectedLabel={frankify ? "On" : "Off"}>
                                    <IconButton selected={frankify === false} title="Frankify Off" onClick={() => setFrankify(false)} src={iconUrl({ custom: null })} imgStyle={iconStyleFor("frankify")} />
                                    <IconButton selected={frankify === true} title="Frankify On" onClick={() => setFrankify(true)} src={iconUrl({ custom: "frank_hat" })} imgStyle={iconStyleFor("frankify")} />
                                </IconRow>
                            </div>

                            <button
                                type="button"
                                onClick={randomise}
                                className="w-full h-12 border border-black bg-maroon text-white hover:opacity-90 font-['Jersey_10'] uppercase text-sm"
                                style={{ boxShadow: "0 3px 0 0 rgba(0,0,0,0.3)" }}
                            >
                                Randomise!
                            </button>
                        </div>

                        <PreviousHabbosPanel className="lg:hidden mt-6" />

                        <div className="hidden lg:block flex-1" />
                    </div>
                </div>
            </ContentBox>
        </div>
    );
}

export default HabboImager;